const INTENT_SET = new Set([
  'profitability_guide',
  'property_value',
  'property_sale',
  'legacy_owner_response',
]);

const UNIT_MIX_KEYS = [
  'oneAndHalf',
  'twoAndHalf',
  'threeAndHalf',
  'fourAndHalf',
  'fiveAndHalf',
  'sixAndHalfPlus',
  'commercial',
  'other',
];

const EXPENSE_KEYS_ALLOW_NULL = new Set([
  'municipalTaxesCents',
  'schoolTaxesCents',
  'insuranceCents',
  'heatingElectricityCents',
]);

const EXPENSE_KEYS = [
  'municipalTaxesCents',
  'schoolTaxesCents',
  'insuranceCents',
  'heatingElectricityCents',
  'otherExpensesCents',
];

const PROVENANCE_KEYS = new Set([
  'sourceType',
  'campaignId',
  'mailingId',
  'recipientOrdinal',
  'responseIntent',
  'responseSchemaVersion',
  'submittedAt',
  'projectedAt',
]);

const IMPORT_PROVENANCE_KEYS = new Set(['sourceType', 'importId', 'importedAt']);
const IMPORT_PROVENANCE_TYPES = new Set(['jlr_csv', 'generic_csv', 'admin_edit']);
const REGISTRY_ACTIVITY_TYPES = new Set([
  'sale',
  'mortgage',
  'transmission',
  'assignment_or_donation',
  'undivided_sale',
  'sixty_day_notice',
  'correction_assignment',
  'other',
]);
const LAND_AREA_UNITS = new Set(['m2', 'ft2']);

const DETAIL_KEYS = new Set([
  'id',
  'address',
  'city',
  'province',
  'postalCode',
  'numberOfUnits',
  'ownerName',
  'status',
  'source',
  'lastContactAt',
  'nextFollowUpAt',
  'createdAt',
  'updatedAt',
  'updatedAtVersion',
  'ownerMailingAddress',
  'notes',
  'createdByUid',
  'updatedByUid',
  'owners',
  'primaryContact',
  'latestOwnerProvidedPropertyProfile',
  'latestOwnerProvidedFinancialSnapshot',
  'publicResponseSummary',
  'latestImportedPropertyProfile',
  'latestMunicipalAssessment',
  'latestRegistryActivity',
  'lotNumbers',
  'cadastralDivision',
  'municipalRollNumber',
  'convertedPropertyId',
  'convertedAt',
]);

const LIST_FORBIDDEN = [
  'primaryContact',
  'latestOwnerProvidedPropertyProfile',
  'latestOwnerProvidedFinancialSnapshot',
  'publicResponseSummary',
  'latestImportedPropertyProfile',
  'latestMunicipalAssessment',
  'latestRegistryActivity',
  'lotNumbers',
  'cadastralDivision',
  'municipalRollNumber',
  'lotMatchKeys',
  'ownerMailingAddress',
  'notes',
  'email',
  'phone',
  'grossAnnualIncomeCents',
];

function optionalIso(value) {
  if (value == null) return null;
  return typeof value === 'string' ? value : null;
}

function optionalString(value) {
  return typeof value === 'string' && value.trim() ? value : null;
}

function parseCents(value, { allowNull = false } = {}) {
  if (value === null) {
    if (allowNull) return null;
    return null;
  }
  if (!Number.isInteger(value) || value < 0) return null;
  return value;
}

function parseProvenance(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  for (const key of Object.keys(raw)) {
    if (!PROVENANCE_KEYS.has(key)) return null;
  }
  if (raw.sourceType !== 'public_qr_response') return null;
  if (typeof raw.campaignId !== 'string' || !raw.campaignId) return null;
  if (typeof raw.mailingId !== 'string' || !raw.mailingId) return null;
  if (!INTENT_SET.has(raw.responseIntent)) return null;
  if (!Number.isInteger(raw.responseSchemaVersion)) return null;
  const submittedAt = optionalIso(raw.submittedAt);
  const projectedAt = optionalIso(raw.projectedAt);
  if (!submittedAt || !projectedAt) return null;
  return {
    sourceType: 'public_qr_response',
    campaignId: raw.campaignId,
    mailingId: raw.mailingId,
    recipientOrdinal: Number.isInteger(raw.recipientOrdinal) ? raw.recipientOrdinal : 0,
    responseIntent: raw.responseIntent,
    responseSchemaVersion: raw.responseSchemaVersion,
    submittedAt,
    projectedAt,
  };
}

function parseImportProvenance(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  for (const key of Object.keys(raw)) {
    if (!IMPORT_PROVENANCE_KEYS.has(key)) return null;
  }
  if (!IMPORT_PROVENANCE_TYPES.has(raw.sourceType)) return null;
  const importedAt = optionalIso(raw.importedAt);
  if (!importedAt) return null;
  return {
    sourceType: raw.sourceType,
    importId: typeof raw.importId === 'string' && raw.importId.trim() ? raw.importId : null,
    importedAt,
  };
}

function parseLandArea(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (!LAND_AREA_UNITS.has(raw.unit)) return null;
  const value = typeof raw.value === 'number' && Number.isFinite(raw.value) && raw.value > 0 ? raw.value : null;
  const squareMetres =
    typeof raw.squareMetres === 'number' && Number.isFinite(raw.squareMetres) && raw.squareMetres > 0
      ? raw.squareMetres
      : null;
  if (value == null || squareMetres == null) return null;
  return { value, unit: raw.unit, squareMetres };
}

function parseImportedPropertyProfile(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const provenance = parseImportProvenance(raw.provenance);
  if (!provenance) return null;
  const lotNumbers = Array.isArray(raw.lotNumbers)
    ? raw.lotNumbers.filter((lot) => typeof lot === 'string' && lot.trim())
    : [];
  const landArea = raw.landArea ? parseLandArea(raw.landArea) : null;
  const profile = {
    buildingCategory: optionalString(raw.buildingCategory),
    constructionType: optionalString(raw.constructionType),
    numberOfUnits: Number.isInteger(raw.numberOfUnits) && raw.numberOfUnits > 0 ? raw.numberOfUnits : null,
    numberOfFloors: Number.isInteger(raw.numberOfFloors) && raw.numberOfFloors > 0 ? raw.numberOfFloors : null,
    constructionYear:
      Number.isInteger(raw.constructionYear) && raw.constructionYear > 0 ? raw.constructionYear : null,
    landArea,
    lotNumbers,
    cadastralDivision: optionalString(raw.cadastralDivision),
    municipalRollNumber: optionalString(raw.municipalRollNumber),
    provenance,
  };
  const hasData =
    profile.buildingCategory ||
    profile.constructionType ||
    profile.numberOfUnits != null ||
    profile.numberOfFloors != null ||
    profile.constructionYear != null ||
    profile.landArea ||
    profile.lotNumbers.length ||
    profile.cadastralDivision ||
    profile.municipalRollNumber;
  return hasData ? profile : null;
}

function parseMunicipalAssessment(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const provenance = parseImportProvenance(raw.provenance);
  if (!provenance) return null;
  const centsField = (value) =>
    value === null ? null : Number.isInteger(value) && value >= 0 ? value : null;
  const totalCents = centsField(raw.totalCents);
  const landCents = centsField(raw.landCents);
  const buildingCents = centsField(raw.buildingCents);
  const referenceYear =
    raw.referenceYear === null
      ? null
      : Number.isInteger(raw.referenceYear) && raw.referenceYear > 0
        ? raw.referenceYear
        : null;
  const referenceDate = raw.referenceDate === null ? null : optionalString(raw.referenceDate);
  if (
    totalCents == null &&
    landCents == null &&
    buildingCents == null &&
    referenceYear == null &&
    referenceDate == null
  ) {
    return null;
  }
  return {
    totalCents,
    landCents,
    buildingCents,
    referenceYear,
    referenceDate,
    provenance,
  };
}

function parseRegistryActivity(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const provenance = parseImportProvenance(raw.provenance);
  if (!provenance) return null;
  const activityType = REGISTRY_ACTIVITY_TYPES.has(raw.activityType) ? raw.activityType : null;
  const originalLabel = optionalString(raw.originalLabel);
  const activityDate = optionalString(raw.activityDate);
  const considerationCents =
    raw.considerationCents === null
      ? null
      : Number.isInteger(raw.considerationCents) && raw.considerationCents >= 0
        ? raw.considerationCents
        : null;
  const registrationNumber = optionalString(raw.registrationNumber);
  const transferredPercentage =
    raw.transferredPercentage === null
      ? null
      : typeof raw.transferredPercentage === 'number' &&
          Number.isFinite(raw.transferredPercentage) &&
          raw.transferredPercentage >= 0 &&
          raw.transferredPercentage <= 100
        ? raw.transferredPercentage
        : null;
  if (
    !activityType &&
    !originalLabel &&
    !activityDate &&
    considerationCents == null &&
    !registrationNumber &&
    transferredPercentage == null
  ) {
    return null;
  }
  return {
    activityType,
    originalLabel,
    activityDate,
    considerationCents,
    registrationNumber,
    transferredPercentage,
    provenance,
  };
}

function parseLotNumbers(raw) {
  if (!Array.isArray(raw)) return null;
  const lotNumbers = raw.filter((lot) => typeof lot === 'string' && lot.trim());
  return lotNumbers.length ? lotNumbers : null;
}

function parsePrimaryContact(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const fullName = optionalString(raw.fullName);
  const email = typeof raw.email === 'string' && raw.email.trim() ? raw.email.trim().toLowerCase() : null;
  if (!fullName || !email) return null;
  const provenance = parseProvenance(raw.provenance);
  if (!provenance) return null;
  return {
    fullName,
    email,
    phone: raw.phone == null ? null : optionalString(raw.phone),
    verificationState: raw.verificationState === 'admin_verified' ? 'admin_verified' : 'unverified',
    firstCapturedAt: optionalIso(raw.firstCapturedAt),
    updatedAt: optionalIso(raw.updatedAt),
    provenance,
  };
}

function parsePropertyProfile(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const provenance = parseProvenance(raw.provenance);
  if (!provenance) return null;
  const unitMixIn = raw.unitMix && typeof raw.unitMix === 'object' ? raw.unitMix : null;
  if (!unitMixIn) return null;
  const unitMix = {};
  for (const key of UNIT_MIX_KEYS) {
    if (!Number.isInteger(unitMixIn[key]) || unitMixIn[key] < 0) return null;
    unitMix[key] = unitMixIn[key];
  }
  const constructionYearUnknown = raw.constructionYearUnknown === true;
  const constructionYear = constructionYearUnknown
    ? null
    : Number.isInteger(raw.constructionYear)
      ? raw.constructionYear
      : null;
  if (!constructionYearUnknown && constructionYear == null) return null;
  return {
    constructionYear,
    constructionYearUnknown,
    unitMix,
    unitCountMismatchConfirmed: raw.unitCountMismatchConfirmed === true,
    declaredUnitTotal: Number.isInteger(raw.declaredUnitTotal) ? raw.declaredUnitTotal : null,
    importedNumberOfUnits: Number.isInteger(raw.importedNumberOfUnits)
      ? raw.importedNumberOfUnits
      : null,
    verificationState: raw.verificationState === 'admin_verified' ? 'admin_verified' : 'unverified',
    provenance,
  };
}

function parseFinancialSnapshot(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const provenance = parseProvenance(raw.provenance);
  if (!provenance) return null;
  if (raw.currency !== 'CAD' || raw.period !== 'annual_current') return null;
  const gross = parseCents(raw.grossAnnualIncomeCents, { allowNull: false });
  if (gross == null) return null;
  const expensesIn = raw.expenses && typeof raw.expenses === 'object' ? raw.expenses : null;
  if (!expensesIn) return null;
  const expenses = {};
  for (const key of EXPENSE_KEYS) {
    const value = expensesIn[key];
    if (value === null) {
      if (!EXPENSE_KEYS_ALLOW_NULL.has(key)) return null;
      expenses[key] = null;
      continue;
    }
    if (!Number.isInteger(value) || value < 0) return null;
    expenses[key] = value;
  }
  return {
    currency: 'CAD',
    period: 'annual_current',
    grossAnnualIncomeCents: gross,
    expenses,
    otherExpensesDescription:
      typeof raw.otherExpensesDescription === 'string' ? raw.otherExpensesDescription : null,
    verificationState: raw.verificationState === 'admin_verified' ? 'admin_verified' : 'unverified',
    provenance,
  };
}

function parsePublicResponseSummary(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const receivedIntents = {};
  const source = raw.receivedIntents && typeof raw.receivedIntents === 'object' ? raw.receivedIntents : {};
  for (const intent of INTENT_SET) {
    const entry = source[intent];
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    if (entry.status !== 'received') continue;
    receivedIntents[intent] = {
      status: 'received',
      firstSubmittedAt: optionalIso(entry.firstSubmittedAt),
      lastSubmittedAt: optionalIso(entry.lastSubmittedAt),
    };
  }
  return {
    firstRespondedAt: optionalIso(raw.firstRespondedAt),
    lastRespondedAt: optionalIso(raw.lastRespondedAt),
    lastResponseIntent: INTENT_SET.has(raw.lastResponseIntent) ? raw.lastResponseIntent : null,
    lastCampaignId: typeof raw.lastCampaignId === 'string' ? raw.lastCampaignId : null,
    lastMailingId: typeof raw.lastMailingId === 'string' ? raw.lastMailingId : null,
    receivedIntents,
  };
}

export function parseGetAdminProspect(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const out = {};
  for (const key of DETAIL_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
    if (key === 'primaryContact') {
      const parsed = parsePrimaryContact(data.primaryContact);
      if (parsed) out.primaryContact = parsed;
      continue;
    }
    if (key === 'latestOwnerProvidedPropertyProfile') {
      const parsed = parsePropertyProfile(data.latestOwnerProvidedPropertyProfile);
      if (parsed) out.latestOwnerProvidedPropertyProfile = parsed;
      continue;
    }
    if (key === 'latestOwnerProvidedFinancialSnapshot') {
      const parsed = parseFinancialSnapshot(data.latestOwnerProvidedFinancialSnapshot);
      if (parsed) out.latestOwnerProvidedFinancialSnapshot = parsed;
      continue;
    }
    if (key === 'publicResponseSummary') {
      const parsed = parsePublicResponseSummary(data.publicResponseSummary);
      if (parsed) out.publicResponseSummary = parsed;
      continue;
    }
    if (key === 'latestImportedPropertyProfile') {
      const parsed = parseImportedPropertyProfile(data.latestImportedPropertyProfile);
      if (parsed) out.latestImportedPropertyProfile = parsed;
      continue;
    }
    if (key === 'latestMunicipalAssessment') {
      const parsed = parseMunicipalAssessment(data.latestMunicipalAssessment);
      if (parsed) out.latestMunicipalAssessment = parsed;
      continue;
    }
    if (key === 'latestRegistryActivity') {
      const parsed = parseRegistryActivity(data.latestRegistryActivity);
      if (parsed) out.latestRegistryActivity = parsed;
      continue;
    }
    if (key === 'lotNumbers') {
      const parsed = parseLotNumbers(data.lotNumbers);
      if (parsed) out.lotNumbers = parsed;
      continue;
    }
    if (key === 'updatedAtVersion') {
      const raw = data.updatedAtVersion;
      if (
        raw &&
        typeof raw === 'object' &&
        Number.isInteger(raw.s) &&
        Number.isInteger(raw.n) &&
        raw.n >= 0 &&
        raw.n < 1e9
      ) {
        out.updatedAtVersion = { s: raw.s, n: raw.n };
      }
      continue;
    }
    if (key === 'cadastralDivision' || key === 'municipalRollNumber') {
      const parsed = optionalString(data[key]);
      if (parsed) out[key] = parsed;
      continue;
    }
    if (key === 'convertedPropertyId') {
      const raw = data.convertedPropertyId;
      if (typeof raw === 'string' && raw.trim()) {
        out.convertedPropertyId = raw.trim();
      }
      continue;
    }
    if (key === 'convertedAt') {
      const parsed = optionalIso(data.convertedAt);
      if (parsed) out.convertedAt = parsed;
      continue;
    }
    out[key] = data[key];
  }
  return out;
}

export function parseAdminProspectListItem(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const json = JSON.stringify(data);
  for (const key of LIST_FORBIDDEN) {
    if (Object.prototype.hasOwnProperty.call(data, key) || json.includes(key)) {
      throw new TypeError('List DTO leaked CRM projection fields');
    }
  }
  return data;
}

export function parseAdminProspectActivity(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const type = typeof item.type === 'string' ? item.type : null;
  const out = {
    id: typeof item.id === 'string' ? item.id : null,
    type,
    createdAt: optionalIso(item.createdAt),
    createdByUid: typeof item.createdByUid === 'string' ? item.createdByUid : null,
  };
  if (type === 'note') {
    out.body = typeof item.body === 'string' ? item.body : null;
  }
  if (type === 'status_change') {
    out.fromStatus = typeof item.fromStatus === 'string' ? item.fromStatus : null;
    out.toStatus = typeof item.toStatus === 'string' ? item.toStatus : null;
  }
  if (type === 'public_response') {
    if (typeof item.campaignId === 'string' && item.campaignId) out.campaignId = item.campaignId;
    if (typeof item.mailingId === 'string' && item.mailingId) out.mailingId = item.mailingId;
    if (INTENT_SET.has(item.intent)) out.intent = item.intent;
    if (Number.isInteger(item.schemaVersion)) out.schemaVersion = item.schemaVersion;
    out.submittedAt = optionalIso(item.submittedAt);
    out.projectedContact = item.projectedContact === true;
    out.projectedPropertyProfile = item.projectedPropertyProfile === true;
    out.projectedFinancialSnapshot = item.projectedFinancialSnapshot === true;
    if (typeof item.projectionStatus === 'string') out.projectionStatus = item.projectionStatus;
    out.createdByUid = typeof item.createdByUid === 'string' ? item.createdByUid : null;
  }
  const json = JSON.stringify(out);
  if (
    Object.prototype.hasOwnProperty.call(out, 'email') ||
    Object.prototype.hasOwnProperty.call(out, 'phone') ||
    Object.prototype.hasOwnProperty.call(out, 'fullName') ||
    json.includes('grossAnnualIncomeCents')
  ) {
    throw new TypeError('Activity DTO leaked PII or financial data');
  }
  return out;
}

export function parseListAdminProspectActivities(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { activities: [], nextPageToken: null };
  const source = Array.isArray(data.activities)
    ? data.activities
    : Array.isArray(data.items)
      ? data.items
      : [];
  return {
    activities: source.map(parseAdminProspectActivity).filter(Boolean),
    nextPageToken: typeof data.nextPageToken === 'string' ? data.nextPageToken : null,
  };
}

const IMPORT_HISTORY_ACTION_KINDS = new Set(['create', 'update']);
const IMPORT_HISTORY_CHANGED_FIELDS = new Set([
  'latestImportedPropertyProfile',
  'latestMunicipalAssessment',
  'latestRegistryActivity',
  'lotNumbers',
  'cadastralDivision',
  'municipalRollNumber',
  'numberOfUnits',
  'ownerName',
  'ownerMailingAddress',
  'owners',
]);
const IMPORT_HISTORY_SECTION_OUTCOMES = new Set([
  'preserved_blank',
  'replaced',
  'ignored_older',
  'rejected_conflict',
  'identical',
  'enriched',
]);
const IMPORT_HISTORY_ENTRY_KEYS = new Set([
  'id',
  'importedAt',
  'actionKind',
  'sourceType',
  'changedFields',
  'schemaPartial',
  'orderingKey',
  'snapshot',
]);

function parseHistoryChangedFields(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const field of raw) {
    if (typeof field !== 'string') continue;
    if (!IMPORT_HISTORY_CHANGED_FIELDS.has(field)) continue;
    if (!out.includes(field)) out.push(field);
  }
  return out;
}

function parseHistoryOwners(raw) {
  if (!Array.isArray(raw)) return null;
  const owners = raw.map((owner) => {
    if (!owner || typeof owner !== 'object' || Array.isArray(owner)) return null;
    const out = {};
    if (typeof owner.name === 'string' && owner.name.trim()) out.name = owner.name.trim();
    if (typeof owner.mailingAddress === 'string' && owner.mailingAddress.trim()) {
      out.mailingAddress = owner.mailingAddress.trim();
    }
    if (typeof owner.city === 'string' && owner.city.trim()) out.city = owner.city.trim();
    if (typeof owner.province === 'string' && owner.province.trim()) out.province = owner.province.trim();
    if (typeof owner.postalCode === 'string' && owner.postalCode.trim()) {
      out.postalCode = owner.postalCode.trim();
    }
    return out.name || out.mailingAddress ? out : null;
  });
  return owners.some(Boolean) ? owners : null;
}

const IMPORT_HISTORY_DISPOSITIONS = new Set([
  'replaced',
  'enriched',
  'modified',
  'preserved',
  'unchanged',
  'unknown',
]);

const IMPORT_HISTORY_FORBIDDEN_KEYS = new Set([
  'addressKey',
  'lotMatchKeys',
  'adminSearch',
  'adminTargeting',
  'rawCsv',
  'csvRow',
  'storagePath',
  'downloadToken',
  'reservation',
  'importRowMarker',
  'actorUid',
  'downloadUrl',
  'signedUrl',
  'reservationId',
  'reservationToken',
]);

function parseHistoryOptionalCents(value, { present } = {}) {
  if (!present) return { ok: true, present: false };
  if (value === null) return { ok: true, present: true, value: null };
  if (Number.isInteger(value) && value >= 0) return { ok: true, present: true, value };
  return { ok: false };
}

/** Allowlisted history profile — provenance optional; unknown keys omitted. */
function parseHistoryPropertyProfile(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const provenance = parseImportProvenance(raw.provenance);
  const lotNumbers = Array.isArray(raw.lotNumbers)
    ? raw.lotNumbers.filter((lot) => typeof lot === 'string' && lot.trim()).map((lot) => lot.trim())
    : [];
  const landArea = raw.landArea ? parseLandArea(raw.landArea) : null;
  const profile = {
    buildingCategory: optionalString(raw.buildingCategory),
    constructionType: optionalString(raw.constructionType),
    numberOfUnits: Number.isInteger(raw.numberOfUnits) && raw.numberOfUnits > 0 ? raw.numberOfUnits : null,
    numberOfFloors: Number.isInteger(raw.numberOfFloors) && raw.numberOfFloors > 0 ? raw.numberOfFloors : null,
    constructionYear:
      Number.isInteger(raw.constructionYear) && raw.constructionYear > 0 ? raw.constructionYear : null,
    landArea,
    lotNumbers,
    cadastralDivision: optionalString(raw.cadastralDivision),
    municipalRollNumber: optionalString(raw.municipalRollNumber),
  };
  if (provenance) profile.provenance = provenance;
  const hasData =
    profile.buildingCategory ||
    profile.constructionType ||
    profile.numberOfUnits != null ||
    profile.numberOfFloors != null ||
    profile.constructionYear != null ||
    profile.landArea ||
    profile.lotNumbers.length ||
    profile.cadastralDivision ||
    profile.municipalRollNumber;
  return hasData ? profile : null;
}

/** Partial assessments allowed; missing land/building omitted (not zeroed). */
function parseHistoryMunicipalAssessment(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const total = parseHistoryOptionalCents(raw.totalCents, {
    present: Object.prototype.hasOwnProperty.call(raw, 'totalCents'),
  });
  const land = parseHistoryOptionalCents(raw.landCents, {
    present: Object.prototype.hasOwnProperty.call(raw, 'landCents'),
  });
  const building = parseHistoryOptionalCents(raw.buildingCents, {
    present: Object.prototype.hasOwnProperty.call(raw, 'buildingCents'),
  });
  if (!total.ok || !land.ok || !building.ok) return null;

  let referenceYear;
  if (!Object.prototype.hasOwnProperty.call(raw, 'referenceYear')) referenceYear = undefined;
  else if (raw.referenceYear === null) referenceYear = null;
  else if (Number.isInteger(raw.referenceYear) && raw.referenceYear > 0) referenceYear = raw.referenceYear;
  else return null;

  let referenceDate;
  if (!Object.prototype.hasOwnProperty.call(raw, 'referenceDate')) referenceDate = undefined;
  else if (raw.referenceDate === null) referenceDate = null;
  else if (typeof raw.referenceDate === 'string' && raw.referenceDate.trim()) {
    referenceDate = raw.referenceDate.trim();
  } else return null;

  const out = {};
  if (total.present) out.totalCents = total.value;
  if (land.present) out.landCents = land.value;
  if (building.present) out.buildingCents = building.value;
  if (referenceYear !== undefined) out.referenceYear = referenceYear;
  if (referenceDate !== undefined) out.referenceDate = referenceDate;
  if (Object.prototype.hasOwnProperty.call(raw, 'provenance')) {
    const provenance = parseImportProvenance(raw.provenance);
    if (provenance) out.provenance = provenance;
  }
  if (
    !Object.prototype.hasOwnProperty.call(out, 'totalCents') &&
    !Object.prototype.hasOwnProperty.call(out, 'landCents') &&
    !Object.prototype.hasOwnProperty.call(out, 'buildingCents') &&
    !Object.prototype.hasOwnProperty.call(out, 'referenceYear') &&
    !Object.prototype.hasOwnProperty.call(out, 'referenceDate')
  ) {
    return null;
  }
  return out;
}

function parseHistoryRegistryActivity(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const activityType = REGISTRY_ACTIVITY_TYPES.has(raw.activityType) ? raw.activityType : null;
  const originalLabel = optionalString(raw.originalLabel);
  const activityDate = optionalString(raw.activityDate);
  const considerationCents =
    raw.considerationCents === null
      ? null
      : Number.isInteger(raw.considerationCents) && raw.considerationCents >= 0
        ? raw.considerationCents
        : raw.considerationCents == null
          ? null
          : undefined;
  if (considerationCents === undefined) return null;
  const registrationNumber = optionalString(raw.registrationNumber);
  const transferredPercentage =
    raw.transferredPercentage === null
      ? null
      : typeof raw.transferredPercentage === 'number' &&
          Number.isFinite(raw.transferredPercentage) &&
          raw.transferredPercentage >= 0 &&
          raw.transferredPercentage <= 100
        ? raw.transferredPercentage
        : raw.transferredPercentage == null
          ? null
          : undefined;
  if (transferredPercentage === undefined) return null;
  if (
    !activityType &&
    !originalLabel &&
    !activityDate &&
    considerationCents == null &&
    !registrationNumber &&
    transferredPercentage == null
  ) {
    return null;
  }
  const out = {
    activityType,
    originalLabel,
    activityDate,
    considerationCents,
    registrationNumber,
    transferredPercentage,
  };
  if (Object.prototype.hasOwnProperty.call(raw, 'provenance')) {
    const provenance = parseImportProvenance(raw.provenance);
    if (provenance) out.provenance = provenance;
  }
  return out;
}

function parseHistoryRecorded(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const recorded = {};
  const profile = parseHistoryPropertyProfile(raw.latestImportedPropertyProfile);
  if (profile) recorded.latestImportedPropertyProfile = profile;
  const assessment = parseHistoryMunicipalAssessment(raw.latestMunicipalAssessment);
  if (assessment) recorded.latestMunicipalAssessment = assessment;
  const registry = parseHistoryRegistryActivity(raw.latestRegistryActivity);
  if (registry) recorded.latestRegistryActivity = registry;
  const lots = parseLotNumbers(raw.lotNumbers);
  if (lots) recorded.lotNumbers = lots;
  if (typeof raw.cadastralDivision === 'string' && raw.cadastralDivision.trim()) {
    recorded.cadastralDivision = raw.cadastralDivision.trim();
  }
  if (typeof raw.municipalRollNumber === 'string' && raw.municipalRollNumber.trim()) {
    recorded.municipalRollNumber = raw.municipalRollNumber.trim();
  }
  if (Number.isInteger(raw.numberOfUnits) && raw.numberOfUnits > 0) {
    recorded.numberOfUnits = raw.numberOfUnits;
  }
  if (typeof raw.ownerName === 'string' && raw.ownerName.trim()) {
    recorded.ownerName = raw.ownerName.trim();
  }
  if (typeof raw.ownerMailingAddress === 'string' && raw.ownerMailingAddress.trim()) {
    recorded.ownerMailingAddress = raw.ownerMailingAddress.trim();
  }
  const owners = parseHistoryOwners(raw.owners);
  if (owners) recorded.owners = owners;
  return recorded;
}

function parseHistorySectionDispositions(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const key of [
    'latestImportedPropertyProfile',
    'latestMunicipalAssessment',
    'latestRegistryActivity',
    'owners',
  ]) {
    if (IMPORT_HISTORY_DISPOSITIONS.has(raw[key])) out[key] = raw[key];
  }
  return out;
}

function assertHistoryDtoKeysSafe(value) {
  if (Array.isArray(value)) {
    value.forEach((entry) => assertHistoryDtoKeysSafe(entry));
    return;
  }
  if (!value || typeof value !== 'object') return;
  for (const key of Object.keys(value)) {
    if (IMPORT_HISTORY_FORBIDDEN_KEYS.has(key)) {
      throw new TypeError('Import history DTO leaked internal fields');
    }
    assertHistoryDtoKeysSafe(value[key]);
  }
}

function parseHistorySnapshot(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const sectionOutcomesRaw =
    raw.sectionOutcomes && typeof raw.sectionOutcomes === 'object' ? raw.sectionOutcomes : null;
  let sectionOutcomes = null;
  if (sectionOutcomesRaw) {
    const assessment = IMPORT_HISTORY_SECTION_OUTCOMES.has(sectionOutcomesRaw.latestMunicipalAssessment)
      ? sectionOutcomesRaw.latestMunicipalAssessment
      : null;
    const registry = IMPORT_HISTORY_SECTION_OUTCOMES.has(sectionOutcomesRaw.latestRegistryActivity)
      ? sectionOutcomesRaw.latestRegistryActivity
      : null;
    if (assessment || registry) {
      sectionOutcomes = {
        latestMunicipalAssessment: assessment,
        latestRegistryActivity: registry,
      };
    }
  }

  return {
    operation: IMPORT_HISTORY_ACTION_KINDS.has(raw.operation) ? raw.operation : null,
    changedFields: parseHistoryChangedFields(raw.changedFields),
    sectionOutcomes,
    provenance: parseImportProvenance(raw.provenance),
    recorded: parseHistoryRecorded(raw.recorded),
    sectionDispositions: parseHistorySectionDispositions(raw.sectionDispositions),
  };
}

export function parseAdminProspectImportHistoryEntry(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const out = {};
  for (const key of IMPORT_HISTORY_ENTRY_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(item, key)) continue;
    if (key === 'id') {
      out.id = typeof item.id === 'string' ? item.id : null;
      continue;
    }
    if (key === 'importedAt') {
      out.importedAt = optionalIso(item.importedAt);
      continue;
    }
    if (key === 'actionKind') {
      out.actionKind = IMPORT_HISTORY_ACTION_KINDS.has(item.actionKind) ? item.actionKind : null;
      continue;
    }
    if (key === 'sourceType') {
      out.sourceType = IMPORT_PROVENANCE_TYPES.has(item.sourceType) ? item.sourceType : null;
      continue;
    }
    if (key === 'changedFields') {
      out.changedFields = parseHistoryChangedFields(item.changedFields);
      continue;
    }
    if (key === 'schemaPartial') {
      out.schemaPartial = item.schemaPartial === true;
      continue;
    }
    if (key === 'orderingKey') {
      out.orderingKey = item.orderingKey === 'importedAt' ? 'importedAt' : null;
      continue;
    }
    if (key === 'snapshot') {
      out.snapshot = parseHistorySnapshot(item.snapshot);
    }
  }
  if (!out.id) return null;
  assertHistoryDtoKeysSafe(out);
  return out;
}

export function parseListAdminProspectImportHistory(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { entries: [], nextPageToken: null, orderingCoverage: null };
  }
  const source = Array.isArray(data.entries) ? data.entries : [];
  return {
    entries: source.map(parseAdminProspectImportHistoryEntry).filter(Boolean),
    nextPageToken: typeof data.nextPageToken === 'string' ? data.nextPageToken : null,
    orderingCoverage:
      data.orderingCoverage === 'importedAt_present_only' ? 'importedAt_present_only' : null,
  };
}

const ASSOCIATED_CAMPAIGN_ENTRY_KEYS = new Set([
  'campaignId',
  'name',
  'createdAt',
  'includedAt',
  'status',
  'buildStatus',
]);

const ASSOCIATED_CAMPAIGN_BUILD_STATUSES = new Set(['building', 'ready', 'failed']);

/** Object-key deny list — never scan string values for substrings. */
const ASSOCIATED_CAMPAIGN_FORBIDDEN_KEYS = new Set([
  'snapshot',
  'owner',
  'owners',
  'mailingReadiness',
  'sourceProspectCreatedAt',
  'sequence',
  'qrToken',
  'tokenHash',
  'publicAccess',
  'storagePath',
  'downloadToken',
  'downloadUrl',
  'signedUrl',
  'workerLease',
  'workerLeaseId',
  'workerLeaseExpiresAt',
  'createdBy',
  'targetingSnapshot',
  'prospectIds',
  'excludedProspectIds',
  'adminSearch',
  'adminTargeting',
  'letterTemplateSnapshot',
  'failureCode',
]);

/**
 * Structural DTO safety (PR #290 style): walk object keys only.
 * Legitimate campaign names may contain forbidden substrings
 * ("Homeowners campaign", "Autumn snapshot") without failing.
 */
function assertAssociatedCampaignDtoKeysSafe(value) {
  if (Array.isArray(value)) {
    value.forEach((entry) => assertAssociatedCampaignDtoKeysSafe(entry));
    return;
  }
  if (!value || typeof value !== 'object') return;
  for (const key of Object.keys(value)) {
    if (ASSOCIATED_CAMPAIGN_FORBIDDEN_KEYS.has(key)) {
      throw new TypeError('Associated campaigns DTO leaked internal fields');
    }
    if (!ASSOCIATED_CAMPAIGN_ENTRY_KEYS.has(key)) {
      throw new TypeError(`Associated campaigns DTO unexpected key: ${key}`);
    }
    assertAssociatedCampaignDtoKeysSafe(value[key]);
  }
}

export function parseAdminProspectAssociatedCampaignEntry(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const out = {};
  for (const key of ASSOCIATED_CAMPAIGN_ENTRY_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(item, key)) continue;
    if (key === 'campaignId') {
      out.campaignId = typeof item.campaignId === 'string' ? item.campaignId.trim() : null;
      continue;
    }
    if (key === 'name') {
      out.name = typeof item.name === 'string' ? item.name : null;
      continue;
    }
    if (key === 'createdAt') {
      out.createdAt = optionalIso(item.createdAt);
      continue;
    }
    if (key === 'includedAt') {
      out.includedAt = optionalIso(item.includedAt);
      continue;
    }
    if (key === 'status') {
      out.status = typeof item.status === 'string' ? item.status : null;
      continue;
    }
    if (key === 'buildStatus') {
      out.buildStatus = ASSOCIATED_CAMPAIGN_BUILD_STATUSES.has(item.buildStatus)
        ? item.buildStatus
        : typeof item.buildStatus === 'string'
          ? item.buildStatus
          : null;
    }
  }
  if (!out.campaignId) return null;
  assertAssociatedCampaignDtoKeysSafe(out);
  return out;
}

export function parseListAdminProspectAssociatedCampaigns(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { entries: [], nextPageToken: null, orderingCoverage: null };
  }
  const source = Array.isArray(data.entries) ? data.entries : [];
  return {
    entries: source.map(parseAdminProspectAssociatedCampaignEntry).filter(Boolean),
    nextPageToken: typeof data.nextPageToken === 'string' ? data.nextPageToken : null,
    orderingCoverage:
      data.orderingCoverage === 'snapshotCreatedAt_present_only'
        ? 'snapshotCreatedAt_present_only'
        : null,
  };
}
